/**
 * Menu and scoreboard screens. Like the game renderer, this draws a view model
 * and owns no state beyond a caret blink.
 *
 * Both screens are built from the same chamfered block as the playfield: the
 * name field is the inset variant, the actions are block buttons, and rank
 * badges are palette blocks with the numeral punched into them.
 *
 * Since the mode system landed, mode is a real decision on both screens. The
 * menu explains the selected mode and previews only its runs; the scoreboard
 * filters, because a TWIN run sums two boards and would otherwise always
 * outrank a comparable SOLO one on the same table.
 */

import { SCOREBOARD, finalizeName, type ScoreEntry } from '../core/scoreboard.ts';
import {
  Painter,
  drawMusicChip,
  drawSoundChip,
  drawThemeChip,
  drawVolumeChip,
  type VolumeLevel,
} from './painter.ts';
import { HitList, type HitId } from './hits.ts';
import type { Surface } from './surface.ts';
import {
  BADGE_COLORS,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  LAYOUT,
  MENU_KEYS,
  SCOREBOARD_LAYOUT as SB,
  TYPE,
  faces,
  formatDate,
  formatScore,
  modeColor,
  onBlock,
  palette,
} from './theme.ts';

/** Rows the menu cursor can sit on. */
export enum MenuRow {
  Name = 0,
  Mode = 1,
  Start = 2,
  Scoreboard = 3,
}

export type GameModeName = 'SOLO' | 'BALANCE' | 'TWIN';

/** Which slice of the board the scoreboard is showing. */
export type ScoreFilter = 'ALL' | GameModeName;

export const SCORE_FILTERS: readonly ScoreFilter[] = ['ALL', 'SOLO', 'BALANCE', 'TWIN'];

export interface MenuView {
  /** Name as typed, before it is finalised. */
  nameDraft: string;
  selected: MenuRow;
  mode: GameModeName;
  /** Top entries for the selected mode, already sorted. */
  entries: readonly ScoreEntry[];
  /** Best score for the typed name, 0 when the player is new. */
  personalBest: number;
  /** False when scores only last for this session. */
  persistent: boolean;
}

export interface ScoreboardView {
  /** Entries matching `filter`, already sorted. */
  entries: readonly ScoreEntry[];
  filter: ScoreFilter;
  /** Entry to highlight — the run that was just saved, if any. */
  highlight: ScoreEntry | null;
  persistent: boolean;
}

const CARET_PERIOD_MS = 1060;
const CONTENT_X = LAYOUT.pad;
const CONTENT_W = CANVAS_WIDTH - LAYOUT.pad * 2;

/** Full-bleed decorative band across the top of the menu. */
const MOSAIC_H = 90;
const MOSAIC_ROWS = 3;
/**
 * The band is a fixed sequence rather than a random one, so it is identical on
 * every load and in every screenshot. Letters index the tetromino palette.
 */
const MOSAIC_KEYS =
  'LSSBBCYYMLLCBSSYYMCCLB' +
  'BBCLLSSYMMCLSSBBYCCMLL' +
  'SLLBBMMCYYSLLBCCSSYMBB' +
  'CCSSLLBBYMMSCCLLBSSYYM';
const MOSAIC_HUES: Record<string, string> = {
  L: '#e95f2e',
  S: '#92c745',
  B: '#4479bd',
  C: '#63c9d8',
  Y: '#efdc38',
  M: '#e72f6f',
};

const LEFT_W = 296;
const COLUMN_GAP = 36;
const RIGHT_X = CONTENT_X + LEFT_W + COLUMN_GAP;
const RIGHT_W = CANVAS_WIDTH - LAYOUT.pad - RIGHT_X;

/**
 * The menu is laid out against absolute baselines rather than a running
 * offset, because the mode description sits between two fixed groups and must
 * not push the actions or the key list around.
 */
const MENU = {
  wordmarkY: 176,
  subtitleY: 200,
  playerLabelY: 224,
  fieldY: 232,
  fieldH: 48,
  bestY: 298,
  modeLabelY: 322,
  modeY: 330,
  modeH: 44,
  modeW: 92,
  modePitch: 102,
  /**
   * Up to three lines of 10px copy wrapped to the left column. The block is
   * top-set rather than centred, so a short description leaves the gap above
   * START rather than pushing the button around.
   */
  descriptionY: 386,
  descriptionPitch: 14,
  startY: 420,
  scoreboardY: 478,
  buttonH: 48,
  listLabelY: 224,
  listRuleY: 232,
  listFirstY: 249,
  listPitch: 32,
  listRows: 6,
  listNoteRuleY: 445,
  listNoteY: 466,
  badge: 24,
} as const;

/** What each mode actually asks of the player. Swaps with the selection. */
const MODE_COPY: Record<GameModeName, string> = {
  SOLO: 'GUIDELINE RULES. SRS KICKS, 7-BAG, HOLD, 5-PIECE PREVIEW.',
  BALANCE:
    'THE WELL RIDES A FULCRUM. LET THE STACK DRIFT OFF CENTRE AND IT TOPPLES — WATCH THE GAUGE.',
  TWIN: 'TWO BOARDS, ONE SET OF KEYS, MIRRORED BAGS. BOTH SCORES ADD UP.',
};

const DESCRIPTION_TYPE = { size: 10, weight: 500, tracking: 0.09 };

const MODE_HINTS: Record<GameModeName, string> = {
  SOLO: 'CLASSIC',
  BALANCE: 'PIVOT',
  TWIN: '2 BOARDS',
};

const MODE_IDS: Record<GameModeName, HitId> = {
  SOLO: 'menu.mode.solo',
  BALANCE: 'menu.mode.balance',
  TWIN: 'menu.mode.twin',
};

const MODES: readonly GameModeName[] = ['SOLO', 'BALANCE', 'TWIN'];

const FILTER_IDS: Record<ScoreFilter, HitId> = {
  ALL: 'board.filter.all',
  SOLO: 'board.filter.solo',
  BALANCE: 'board.filter.balance',
  TWIN: 'board.filter.twin',
};

/** Fixed, so the two exits do not move with the number of entries. */
const SB_BUTTON_H = 42;
const SB_ACTIONS_Y = 576;
const SB_BUTTON_GAP = 14;

const CONTROLS: readonly (readonly [string, string])[] = [
  ['MOVE', '← →'],
  ['SOFT DROP', '↓'],
  ['HARD DROP', 'SPACE'],
  ['ROTATE', 'Z X / ↑'],
  ['ROTATE 180', 'A'],
  ['HOLD', 'C / SHIFT'],
  ['PAUSE', 'ESC'],
  ['RESTART', 'R'],
];

export class MenuRenderer {
  /** Controls painted by the last frame, for pointer hit testing. */
  readonly hits = new HitList();
  private readonly painter: Painter;
  private blinkMs = 0;

  constructor(private readonly surface: Surface) {
    this.painter = surface.painter;
  }

  drawMenu(
    view: MenuView,
    dtMs: number,
    muted = false,
    volume: VolumeLevel = 'medium',
    music = true,
  ): void {
    this.blinkMs = (this.blinkMs + dtMs) % CARET_PERIOD_MS;
    this.surface.begin();
    this.hits.clear();

    const p = this.painter;
    const c = palette();

    this.drawMosaic();
    this.header('MENU', '#efdc38', MOSAIC_H + 30, muted, volume, music);

    // --- wordmark ---------------------------------------------------------
    p.typed('KD-TETRIS', CONTENT_X, MENU.wordmarkY, TYPE.wordmark, c.text);
    p.typed('STACK SOLO · BALANCE CHAOS · BATTLE TWIN', CONTENT_X, MENU.subtitleY, TYPE.subtitle, c.textLabel);

    // --- name field -------------------------------------------------------
    const nameSelected = view.selected === MenuRow.Name;
    p.label('PLAYER', CONTENT_X, MENU.playerLabelY, nameSelected ? c.accent : c.textLabel);

    const fieldY = MENU.fieldY;
    p.bevelRect(CONTENT_X, fieldY, LEFT_W, MENU.fieldH, c.well, 4);
    if (nameSelected) this.selectionRing(CONTENT_X, fieldY, LEFT_W, MENU.fieldH);
    this.hits.add('menu.name', CONTENT_X, fieldY, LEFT_W, MENU.fieldH);

    const empty = view.nameDraft.length === 0;
    const shown = empty ? 'ENTER NAME' : view.nameDraft;
    // With no name typed, the caret sits first and the placeholder follows it.
    const textX = CONTENT_X + 16 + (empty ? 19 : 0);
    const baseline = fieldY + MENU.fieldH / 2 + 7;
    const width = p.text(shown, textX, baseline, {
      size: 19,
      color: empty ? c.textFaint : c.text,
      tracking: 0.16,
      weight: 700,
    });

    if (nameSelected && this.blinkMs < CARET_PERIOD_MS / 2) {
      p.fillRect(empty ? CONTENT_X + 16 : textX + width + 8, baseline + 3, 11, 3, c.accent);
    }

    if (view.personalBest > 0) {
      p.typed('YOUR BEST', CONTENT_X, MENU.bestY, TYPE.footer, c.textFaint);
      p.text(formatScore(view.personalBest), CONTENT_X + LEFT_W, MENU.bestY, {
        size: 12,
        weight: 700,
        tracking: 0.06,
        color: c.accent,
        align: 'right',
      });
    }

    // --- mode -------------------------------------------------------------
    p.label('MODE', CONTENT_X, MENU.modeLabelY, view.selected === MenuRow.Mode ? c.accent : c.textLabel);
    MODES.forEach((mode, i) => {
      this.modeCard(mode, CONTENT_X + i * MENU.modePitch, view.mode === mode);
    });
    if (view.selected === MenuRow.Mode) {
      this.selectionRing(CONTENT_X, MENU.modeY, LEFT_W, MENU.modeH);
    }

    // Mode is a real choice now, so it gets a line explaining what it changes.
    wrap(p, MODE_COPY[view.mode], LEFT_W, DESCRIPTION_TYPE).forEach((line, i) => {
      p.typed(
        line,
        CONTENT_X,
        MENU.descriptionY + i * MENU.descriptionPitch,
        DESCRIPTION_TYPE,
        c.textDim,
      );
    });

    // --- actions ----------------------------------------------------------
    this.actionButton(
      'START GAME',
      'ENTER',
      CONTENT_X,
      MENU.startY,
      LEFT_W,
      MENU.buttonH,
      true,
      view.selected === MenuRow.Start,
      'menu.start',
    );
    this.actionButton(
      'SCOREBOARD',
      '',
      CONTENT_X,
      MENU.scoreboardY,
      LEFT_W,
      MENU.buttonH,
      false,
      view.selected === MenuRow.Scoreboard,
      'menu.scoreboard',
    );

    // --- controls reference ----------------------------------------------
    p.label('KEYS', MENU_KEYS.x, MENU_KEYS.labelY + 8);
    CONTROLS.forEach(([name, keys], index) => {
      const x = MENU_KEYS.x + (index % 2) * MENU_KEYS.colWidth;
      const y = MENU_KEYS.firstY + 8 + Math.floor(index / 2) * MENU_KEYS.pitch;
      p.typed(name, x, y, { size: MENU_KEYS.size, weight: 500, tracking: 0.1 }, c.textFaint);
      p.typed(
        keys,
        x + MENU_KEYS.rowWidth,
        y,
        { size: MENU_KEYS.size, weight: 700, tracking: 0.06 },
        c.textDim,
        'right',
      );
    });

    // --- leaderboard preview, filtered to the selected mode ---------------
    const listRight = RIGHT_X + RIGHT_W;
    p.label('TOP RUNS', RIGHT_X, MENU.listLabelY);
    p.typed(
      `${view.mode} · ${view.entries.length.toString().padStart(2, '0')} / ${SCOREBOARD.capacity}`,
      listRight,
      MENU.listLabelY - 2,
      TYPE.chip,
      modeColor(view.mode),
      'right',
    );
    p.rule(RIGHT_X, MENU.listRuleY, RIGHT_W);

    if (view.entries.length === 0) {
      p.text(`NO ${view.mode} RUNS RECORDED`, RIGHT_X, MENU.listFirstY + 16, {
        size: 12,
        weight: 500,
        tracking: 0.18,
        color: c.textFaint,
      });
    }

    const you = finalizeName(view.nameDraft);
    view.entries.slice(0, MENU.listRows).forEach((entry, i) => {
      const y = MENU.listFirstY + i * MENU.listPitch;
      const mine = entry.name === you && view.nameDraft.trim().length > 0;
      p.badge(
        (i + 1).toString().padStart(2, '0'),
        RIGHT_X,
        y,
        MENU.badge,
        BADGE_COLORS[i % BADGE_COLORS.length],
      );
      const rowBaseline = y + MENU.badge / 2 + 4;
      p.text(entry.name, RIGHT_X + MENU.badge + 10, rowBaseline, {
        size: 12,
        weight: 700,
        tracking: 0.14,
        color: mine ? c.accent : c.textDim,
      });
      p.text(formatScore(entry.score), listRight, rowBaseline, {
        size: 12,
        weight: 700,
        tracking: 0.04,
        color: mine ? c.accent : c.text,
        align: 'right',
      });
    });

    p.rule(RIGHT_X, MENU.listNoteRuleY, RIGHT_W);
    p.typed(
      'MODE BOARDS ARE KEPT SEPARATELY',
      RIGHT_X,
      MENU.listNoteY,
      TYPE.footer,
      c.textFaint,
    );

    this.footer(
      view.selected === MenuRow.Name
        ? 'TYPE TO NAME · ↑↓ SELECT · ENTER CONFIRM · F2 THEME · F3 SOUND · F4 MUSIC'
        : view.selected === MenuRow.Mode
          ? '←→ MODE · ↑↓ SELECT · ENTER CONFIRM · F2 THEME · F3 SOUND · F4 MUSIC'
          : '↑↓ SELECT · ENTER CONFIRM · F2 THEME · F3 SOUND · F4 MUSIC',
      view.persistent,
    );
  }

  drawScoreboard(
    view: ScoreboardView,
    dtMs: number,
    muted = false,
    volume: VolumeLevel = 'medium',
    music = true,
  ): void {
    this.blinkMs = (this.blinkMs + dtMs) % CARET_PERIOD_MS;
    this.surface.begin();
    this.hits.clear();

    const p = this.painter;
    const c = palette();
    this.header('SCOREBOARD', '#63c9d8', LAYOUT.pad + 8, muted, volume, music);

    p.typed('SCOREBOARD', CONTENT_X, 98, TYPE.screenTitle, c.text);

    // --- mode filter ------------------------------------------------------
    const tabType = { size: 10, weight: 800, tracking: 0.18 };
    SCORE_FILTERS.forEach((filter, i) => {
      const active = view.filter === filter;
      const x = SB.tabs.xs[i];
      const w = Math.round(p.measureTyped(filter, tabType) + (SB.tabs.padX + 4) * 2);
      const on = active ? { text: c.onAccent } : { text: c.textDim };
      p.bevelRect(x, SB.tabs.y, w, SB.tabs.h, active ? c.accentFaces : c.panelAlt, 4);
      p.typed(filter, x + SB.tabs.padX + 4, SB.tabs.y + SB.tabs.h / 2 + 4, tabType, on.text);
      this.hits.add(FILTER_IDS[filter], x, SB.tabs.y, w, SB.tabs.h);
    });

    // --- column heads -----------------------------------------------------
    const head = { size: 10, weight: 700, tracking: 0.2 };
    p.typed('#', SB.cols.rank, SB.headY, head, c.textFaint);
    p.typed('NAME', SB.cols.name, SB.headY, head, c.textFaint);
    p.typed('MODE', SB.cols.mode, SB.headY, head, c.textFaint);
    p.typed('SCORE', SB.cols.score, SB.headY, head, c.textFaint, 'right');
    p.typed('LINES', SB.cols.lines, SB.headY, head, c.textFaint, 'right');
    p.typed('LVL', SB.cols.level, SB.headY, head, c.textFaint, 'right');
    p.typed('DATE', SB.cols.date, SB.headY, head, c.textFaint, 'right');
    p.rule(CONTENT_X, SB.ruleY, CONTENT_W);

    if (view.entries.length === 0) {
      p.text(
        view.filter === 'ALL' ? 'NO RUNS RECORDED YET' : `NO ${view.filter} RUNS RECORDED YET`,
        CONTENT_X,
        SB.firstRowY + 40,
        { size: 14, weight: 500, tracking: 0.2, color: c.textFaint },
      );
    }

    view.entries.forEach((entry, i) => {
      const y = SB.firstRowY + i * (SB.rowHeight + SB.rowGap);
      const isHighlight = entry === view.highlight;
      if (isHighlight) p.fillRect(CONTENT_X - 6, y, CONTENT_W + 12, SB.rowHeight, c.highlightRow);

      const baseline = y + SB.rowHeight / 2 + 5;
      p.badge(
        (i + 1).toString().padStart(2, '0'),
        SB.cols.rank,
        y + (SB.rowHeight - SB.badge) / 2,
        SB.badge,
        BADGE_COLORS[i % BADGE_COLORS.length],
      );

      const nameW = p.text(entry.name, SB.cols.name, baseline, {
        size: 13,
        weight: 700,
        tracking: 0.14,
        color: c.text,
      });
      if (isHighlight) {
        p.chip(
          'YOU',
          SB.cols.name + nameW + 10,
          y + (SB.rowHeight - 20) / 2,
          '#efdc38',
          { size: 8, weight: 800, tracking: 0.14 },
          5,
          2,
          2,
        );
      }

      // Which board this run actually belongs to. A TWIN score sums two.
      p.chip(
        entry.mode ?? 'SOLO',
        SB.cols.mode,
        y + (SB.rowHeight - 20) / 2,
        modeColor(entry.mode),
        { size: 8, weight: 800, tracking: 0.12 },
        6,
        2,
        2,
      );

      p.text(formatScore(entry.score), SB.cols.score, baseline, {
        size: 13,
        weight: 700,
        tracking: 0.04,
        color: c.text,
        align: 'right',
      });
      p.text(entry.lines.toString(), SB.cols.lines, baseline, {
        size: 12,
        weight: 500,
        tracking: 0.04,
        color: c.textDim,
        align: 'right',
      });
      p.text(entry.level.toString(), SB.cols.level, baseline, {
        size: 12,
        weight: 500,
        tracking: 0.04,
        color: c.textDim,
        align: 'right',
      });
      p.text(formatDate(entry.playedAt), SB.cols.date, baseline, {
        size: 10,
        weight: 500,
        tracking: 0.06,
        color: c.textFaint,
        align: 'right',
      });
    });

    // The scoreboard's two exits were keyboard-only; give them real blocks.
    const btnW = (CONTENT_W - SB_BUTTON_GAP) / 2;
    this.actionButton(
      'PLAY AGAIN',
      'ENTER',
      CONTENT_X,
      SB_ACTIONS_Y,
      btnW,
      SB_BUTTON_H,
      true,
      false,
      'board.play',
    );
    this.actionButton(
      'BACK TO MENU',
      'ESC',
      CONTENT_X + btnW + SB_BUTTON_GAP,
      SB_ACTIONS_Y,
      btnW,
      SB_BUTTON_H,
      false,
      false,
      'board.back',
    );

    this.footer(
      '←→ FILTER · ESC BACK · ENTER PLAY AGAIN · F2 THEME · F3 SOUND · F4 MUSIC',
      view.persistent,
    );
  }

  // ------------------------------------------------------------------ pieces

  /** 24 × 3 band of palette blocks, full-bleed across the top of the menu. */
  private drawMosaic(): void {
    const p = this.painter;
    const cols = Math.ceil(CANVAS_WIDTH / LAYOUT.cell);
    for (let r = 0; r < MOSAIC_ROWS; r++) {
      for (let col = 0; col < cols; col++) {
        const key = MOSAIC_KEYS[(r * 24 + col) % MOSAIC_KEYS.length];
        p.blockCell(col * LAYOUT.cell, r * LAYOUT.cell, LAYOUT.cell, MOSAIC_HUES[key] ?? '#8c54d0');
      }
    }
  }

  private actionButton(
    label: string,
    key: string,
    x: number,
    y: number,
    w: number,
    h: number,
    primary: boolean,
    selected: boolean,
    id: HitId,
  ): void {
    const p = this.painter;
    const c = palette();
    const color = primary ? '#92c745' : null;
    const on = primary ? onBlock('#92c745') : { text: c.textDim, dim: c.textFaint };
    p.blockButton(x, y, w, h, color === null ? c.panelAlt : faces(color), 5, on.text, on.dim, {
      label,
      labelType: primary ? TYPE.buttonPrimary : TYPE.buttonSecondary,
      hint: key,
      hintType: TYPE.buttonKey,
    });
    if (selected) this.selectionRing(x, y, w, h);
    this.hits.add(id, x, y, w, h);
  }

  /**
   * Name over hint rather than name beside keycap: at this size the stacked
   * form reads as a card you pick, not a button with a shortcut on it.
   */
  private modeCard(mode: GameModeName, x: number, active: boolean): void {
    const p = this.painter;
    const c = palette();
    const color = modeColor(mode);
    const on = active ? onBlock(color) : { text: c.textDim, dim: c.textFaint };
    const y = MENU.modeY;

    p.bevelRect(x, y, MENU.modeW, MENU.modeH, active ? faces(color) : c.panelAlt, 5);
    p.typed(mode, x + 9, y + 15, { size: 12, weight: 800, tracking: 0.12 }, on.text);
    p.typed(MODE_HINTS[mode], x + 9, y + 28, { size: 8, weight: 700, tracking: 0.1 }, on.dim);
    const hitPad = (MENU.modePitch - MENU.modeW) / 2;
    this.hits.add(MODE_IDS[mode], x - hitPad, y, MENU.modeW + hitPad * 2, MENU.modeH);
  }

  /** 3px accent frame just outside a block — which row the cursor is on. */
  private selectionRing(x: number, y: number, w: number, h: number): void {
    const p = this.painter;
    const c = palette();
    const g = 3;
    p.fillRect(x - g, y - g, w + g * 2, g, c.accent);
    p.fillRect(x - g, y + h, w + g * 2, g, c.accent);
    p.fillRect(x - g, y, g, h, c.accent);
    p.fillRect(x + w, y, g, h, c.accent);
  }

  private header(
    status: string,
    chipColor: string,
    baselineY: number,
    muted: boolean,
    volume: VolumeLevel,
    music: boolean,
  ): void {
    const p = this.painter;
    const c = palette();
    p.typed('KD-TETRIS', LAYOUT.pad, baselineY, TYPE.brand, c.textLabel);
    let right = CANVAS_WIDTH - LAYOUT.pad;
    right -= p.headerChip(status, right, baselineY, chipColor).w + 8;
    right -= drawThemeChip(p, this.hits, right, baselineY) + 8;
    right -= drawSoundChip(p, this.hits, right, baselineY, muted) + 8;
    right -= drawMusicChip(p, this.hits, right, baselineY, music) + 8;
    drawVolumeChip(p, this.hits, right, baselineY, volume);
    p.rule(LAYOUT.pad, baselineY + 10, CANVAS_WIDTH - LAYOUT.pad * 2);
  }

  private footer(hint: string, persistent: boolean): void {
    const p = this.painter;
    const c = palette();
    const y = CANVAS_HEIGHT - LAYOUT.pad;
    p.rule(LAYOUT.pad, y - 22, CANVAS_WIDTH - LAYOUT.pad * 2);
    const warning = 'SESSION ONLY · STORAGE BLOCKED';
    const warningW = persistent ? 0 : p.measureTyped(warning, TYPE.footer);
    const maxHintW = CANVAS_WIDTH - LAYOUT.pad * 2 - warningW - (persistent ? 0 : 18);
    p.typed(p.fit(hint, maxHintW, TYPE.footer), LAYOUT.pad, y, TYPE.footer, c.textFaint);
    if (!persistent) {
      p.typed(
        warning,
        CANVAS_WIDTH - LAYOUT.pad,
        y,
        TYPE.footer,
        c.textFaint,
        'right',
      );
    }
  }
}

/** Greedy word wrap against the painter's own tracked measurement. */
function wrap(
  p: Painter,
  value: string,
  maxWidth: number,
  token: { size: number; weight: number; tracking: number },
): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of value.split(' ')) {
    const candidate = line.length === 0 ? word : `${line} ${word}`;
    if (line.length > 0 && p.measureTyped(candidate, token) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line.length > 0) lines.push(line);
  return lines;
}
